import { Checkbox } from "../ui/checkbox";
import { Accordion } from "../common/Accordion";

/**
 * Collapsible Room Details component
 * Used for room-by-room painting configuration
 */
export const CollapsibleRoomDetails = ({ 
  selectedRooms = [],
  roomConfig,
  surfaces,
  sizeOptions,
  drywallOptions,
  roomDetails = {},
  expandedRooms = [],
  onRoomToggle,
  onRoomDetailChange,
  className = ""
}) => {
  if (selectedRooms.length === 0) {
    return (
      <div className="text-center py-8 text-[#1F2933]/60">
        <p>Please go back and select the rooms you need painted.</p>
      </div>
    );
  }
  
  return (
    <div className={`space-y-3 ${className}`}>
      {selectedRooms.map((roomValue) => {
        const roomInfo = roomConfig.find(r => r.value === roomValue);
        const roomLabel = roomInfo?.label || roomValue;
        const isExpanded = expandedRooms.includes(roomValue);
        const roomData = roomDetails[roomValue] || {};
        const hasData = roomData.size || (roomData.surfaces && roomData.surfaces.length > 0);
        
        return (
          <Accordion
            key={roomValue}
            id={roomValue}
            title={roomLabel}
            badge={hasData ? "Configured" : null}
            isExpanded={isExpanded}
            onToggle={() => {
              onRoomToggle(roomValue);
              // Initialize with walls selected when expanding for the first time
              if (!isExpanded && !roomData.surfaces) {
                onRoomDetailChange(roomValue, { ...roomData, surfaces: ['walls'] });
              }
            }}
            testIdPrefix="room"
          >
            <div className="p-4 space-y-4">
              {/* Room Size */}
              <div>
                <label className="block text-sm font-medium text-[#1F2933] mb-2">
                  Room Size
                </label>
                <div className="flex gap-2">
                  {Object.entries(sizeOptions).map(([sizeKey, sizeConfig]) => (
                    <button
                      key={sizeKey}
                      type="button"
                      onClick={() => {
                        onRoomDetailChange(roomValue, { ...roomData, size: sizeKey });
                      }}
                      className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                        roomData.size === sizeKey
                          ? 'bg-[#0FA3B1] text-white'
                          : 'bg-gray-100 text-[#1F2933] hover:bg-gray-200'
                      }`}
                      data-testid={`room-size-${roomValue}-${sizeKey}`}
                    >
                      {sizeKey.charAt(0).toUpperCase() + sizeKey.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Surfaces to Paint */}
              <div>
                <label className="block text-sm font-medium text-[#1F2933] mb-2">
                  Surfaces to Paint
                </label>
                <div className="space-y-2">
                  {surfaces.map((surface) => {
                    const surfaceSelected = (roomData.surfaces || ['walls']).includes(surface.value);
                    return (
                      <label
                        key={surface.value}
                        className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                          surfaceSelected ? 'bg-[#0FA3B1]/10' : 'bg-gray-50 hover:bg-gray-100'
                        }`}
                        data-testid={`room-surface-${roomValue}-${surface.value}`}
                      >
                        <Checkbox
                          checked={surfaceSelected}
                          onCheckedChange={(checked) => {
                            const currentSurfaces = roomData.surfaces || ['walls'];
                            const newSurfaces = checked
                              ? [...currentSurfaces, surface.value]
                              : currentSurfaces.filter(s => s !== surface.value);
                            onRoomDetailChange(roomValue, { ...roomData, surfaces: newSurfaces });
                          }}
                          className="border-2 border-gray-300 data-[state=checked]:bg-[#0FA3B1] data-[state=checked]:border-[#0FA3B1]"
                        />
                        <span className="flex-1 text-sm text-[#1F2933]">{surface.label}</span>
                        {surface.price && surface.price.low > 0 && (
                          <span className="text-xs text-[#1F2933]/50">
                            +${surface.price.low}–${surface.price.high}
                          </span>
                        )}
                      </label>
                    );
                  })}
                </div>
              </div>
              
              {/* Drywall Option */}
              <div>
                <label className="block text-sm font-medium text-[#1F2933] mb-2">
                  Drywall Repairs Needed?
                </label>
                <div className="space-y-2">
                  {drywallOptions.map((option) => (
                    <label
                      key={option.value}
                      className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                        roomData.drywall === option.value ? 'bg-[#0FA3B1]/10' : 'bg-gray-50 hover:bg-gray-100'
                      }`}
                      data-testid={`room-drywall-${roomValue}-${option.value}`}
                    >
                      <input
                        type="radio"
                        name={`drywall-${roomValue}`}
                        checked={roomData.drywall === option.value}
                        onChange={() => {
                          onRoomDetailChange(roomValue, { ...roomData, drywall: option.value });
                        }}
                        className="w-4 h-4 text-[#0FA3B1]"
                      />
                      <span className="flex-1 text-sm text-[#1F2933]">{option.label}</span>
                      {option.price && option.price.low > 0 && (
                        <span className="text-xs text-[#1F2933]/50">
                          +${option.price.low}–${option.price.high}
                        </span>
                      )}
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </Accordion>
        );
      })}
    </div>
  );
};

export default CollapsibleRoomDetails;
